/**
 * ChatSessionListV2 — the flat, chronological V2 session list (bead
 * terra-design-v2-chat-yub.2, Codex specs 1-6). Supersedes the bead .1
 * placeholder.
 *
 * Q2 decision (bead .2's "Resolved" section): this row is built directly on
 * Glaze primitives, chat-local to `shell-next/chat/` — it does NOT claim to
 * be a shared `shell-next/ui/` row. If a shared row later appears there,
 * chat migrates to it.
 *
 * The merge/sort/dedupe (live sessions + `useKeystoneConversations()`
 * history, one flat newest-first list, no account grouping) and the
 * Running/Ready/Stopped precedence are NOT re-derived here — both come from
 * `EnrollQueue/useFlowChatListRows.ts`, the same hook `FlowChatSessionList`
 * (V1) consumes (coding-standards.md: "Derivation belongs in a hook shared
 * by V1 and V2").
 *
 * A11y mirrors V1 so both suites assert the same way: container
 * `role="listbox" aria-label="All chats"`, each row `role="option"
 * aria-selected aria-label="${account}: ${title}"`, Enter/Space select.
 * Stop is a SIBLING of the option (WAI-ARIA listbox forbids an interactive
 * descendant of `option`), named `Stop chat with ${account}`; after Stop
 * fires and the row re-renders without it, focus moves back to that row's
 * option element so a keyboard user is never dropped to `body`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { relativeTime, toDateString } from '../../../account-view/keystone/ConversationList';
import { useKeystoneConversationMeta } from '../../../account-view/keystone/useKeystoneConversationMeta';
import { useFlowAccount } from '../../account/hooks';
import {
  useFlowChatSessionStream,
  type FlowChatSessionSummary,
} from '../../EnrollQueue/FlowChatSessionsProvider';
import { useFlowChatListRows, deriveSessionBadge, effectiveTimestamp, matchesChatRow, type SessionBadge } from '../../EnrollQueue/useFlowChatListRows';
import type { Conversation } from '@clay-run/keystone-contract';
import { EmptyState } from '../ui/EmptyState';
import { RailRow } from '../rail/RailRow';
import { RAIL_PANE_GAP, RAIL_PANE_PADDING, RailSearchRow } from '../rail/RailSearchRow';

export interface ChatSessionListV2Props {
  sessions: FlowChatSessionSummary[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onSelectPersisted: (conversationId: string, accountId: string, accountName: string | null) => void;
  onStop: (sessionId: string) => void;
}

const BADGE_COLOR: Record<SessionBadge, 'blue' | 'green' | 'secondary'> = {
  running: 'blue',
  ready: 'green',
  stopped: 'secondary',
};

const BADGE_LABEL: Record<SessionBadge, string> = {
  running: 'Running',
  ready: 'Ready',
  stopped: 'Stopped',
};

function StatusBadge({ status }: { status: SessionBadge }) {
  return (
    <Badge color={BADGE_COLOR[status]} size="xs">
      {status === 'running' && (
        <span data-testid="chat-session-row-spinner" className="inline-flex mr-1 align-middle">
          <LoadingSpinner size="xs" />
        </span>
      )}
      {BADGE_LABEL[status]}
    </Badge>
  );
}

/** Live session row. Account-label and title derivation mirror V1's
 *  `FlowChatSessionRow` exactly (`FlowChatSessionList.tsx:141-146`) — this
 *  per-row view-only derivation is small enough that both V1 and V2 each
 *  inline it (same as V1 already does for its own live vs. persisted rows),
 *  it is not the merge/sort logic the "Do NOT Create" rule is about. */
function ChatSessionRowV2({
  entry,
  active,
  onSelect,
  onStop,
}: {
  entry: FlowChatSessionSummary;
  active: boolean;
  onSelect: (sessionId: string) => void;
  onStop: (sessionId: string) => void;
}) {
  const stream = useFlowChatSessionStream(entry.sessionId);
  const meta = useKeystoneConversationMeta(entry.conversationId);
  const accountQuery = useFlowAccount(entry.accountName ? null : entry.accountId);
  const accountLabel = entry.accountName || accountQuery.data?.account?.name || entry.accountId;

  const title = entry.conversationId
    ? meta.title ?? 'New conversation'
    : entry.pendingFirstMessage ?? 'New chat';

  const badge = deriveSessionBadge(entry, stream?.isRunning);
  const when = relativeTime(toDateString(new Date(entry.lastActivityAt)));

  // Focus-return-after-Stop (Codex spec 3 / coding-standards.md's "keyboard
  // + focus with Stop as a sibling"): a ref rather than derived state
  // because it tracks an EVENT ("this row just asked to stop"), not a
  // render-derivable value — reset at effect-top per StrictMode-resilient-
  // effects discipline (it only ever needs resetting forward, on the next
  // stop request, so there is no separate "top of mount effect" case here).
  const optionRef = useRef<HTMLDivElement>(null);
  const stopRequestedRef = useRef(false);
  useEffect(() => {
    if (stopRequestedRef.current && badge !== 'running') {
      stopRequestedRef.current = false;
      optionRef.current?.focus();
    }
  }, [badge]);

  const select = () => onSelect(entry.sessionId);

  return (
    <RailRow
      variant="flat"
      role="option"
      selected={active}
      rowRef={optionRef}
      aria-label={`${accountLabel}: ${title}`}
      onToggle={select}
      title={title}
      meta={<span className="truncate">{`${accountLabel} · ${when}`}</span>}
      trailing={<StatusBadge status={badge} />}
      aside={
        badge === 'running' ? (
          <Button
            type="button"
            variant="danger-secondary"
            size="xs"
            aria-label={`Stop chat with ${accountLabel}`}
            onClick={(e) => {
              e.stopPropagation();
              stopRequestedRef.current = true;
              onStop(entry.sessionId);
            }}
            className="mr-2 shrink-0"
          >
            Stop
          </Button>
        ) : undefined
      }
    />
  );
}

/** Persisted-only row — no live session in the dock yet, so no status
 *  badge; clicking routes through `onSelectPersisted`. Mirrors V1's
 *  `PersistedConversationRow`. */
function PersistedConversationRowV2({
  conversation,
  onSelect,
}: {
  conversation: Conversation;
  onSelect: (conversationId: string, accountId: string, accountName: string | null) => void;
}) {
  const accountId = conversation.selectedAccountId;
  const accountQuery = useFlowAccount(conversation.accountName ? null : accountId);
  const accountLabel = conversation.accountName || accountQuery.data?.account?.name || accountId || 'Unknown account';
  const title = conversation.title ?? 'Untitled';
  // Same effective-timestamp rule the merge hook used to order this row —
  // NOT re-derived: a naive `new Date(conversation.updatedAt ?? ...)` would
  // throw on a malformed non-null `updatedAt` (`toDateString` calling
  // `.toISOString()` on an Invalid Date), which `effectiveTimestamp`
  // already guards against.
  const when = relativeTime(toDateString(new Date(effectiveTimestamp(conversation))));

  const select = () => onSelect(conversation.id, accountId ?? '', conversation.accountName);

  return (
    <RailRow
      variant="flat"
      role="option"
      selected={false}
      aria-label={`${accountLabel}: ${title}`}
      onToggle={select}
      title={title}
      meta={<span className="truncate">{`${accountLabel} · ${when}`}</span>}
    />
  );
}

export function ChatSessionListV2({ sessions, activeSessionId, onSelect, onSelectPersisted, onStop }: ChatSessionListV2Props) {
  const rows = useFlowChatListRows(sessions, true);
  const [query, setQuery] = useState('');
  const visibleRows = useMemo(() => rows.filter(row => matchesChatRow(row, query)), [rows, query]);

  return (
    <div className={`flex flex-col ${RAIL_PANE_GAP} ${RAIL_PANE_PADDING}`}>
      <RailSearchRow aria-label="Search chats" placeholder="Search chats" onSearch={setQuery} />
      <div role="listbox" aria-label="All chats" data-testid="chat-session-list-v2" className="flex flex-col">
        {rows.length === 0 ? (
          <div data-testid="chat-session-list-v2-empty" className="px-3 py-6 text-center text-[12px] text-content-tertiary">
            No chats yet
          </div>
        ) : visibleRows.length === 0 ? (
          <EmptyState title="No matches" description={`Nothing matches "${query.trim()}".`} />
        ) : (
          visibleRows.map((row) =>
            row.kind === 'live' ? (
              <ChatSessionRowV2
                key={row.entry.sessionId}
                entry={row.entry}
                active={row.entry.sessionId === activeSessionId}
                onSelect={onSelect}
                onStop={onStop}
              />
            ) : (
              <PersistedConversationRowV2 key={row.conversation.id} conversation={row.conversation} onSelect={onSelectPersisted} />
            ),
          )
        )}
      </div>
    </div>
  );
}
